import type { DemoRuleLogicDefinition } from "../rules/ruleLogicDefinitions";
import type { RuleEditorSurface, RuleIntegrationStatus } from "../domain/ruleCatalog";
import {
    describeRuleIntegrationStatus,
    isIntegratedStatus,
} from "../domain/ruleCatalog";
import type {
    RuleEvaluationComparison,
    RuleEvaluationResult,
    RuleRuntimeEvidence,
} from "../domain/ruleEvaluation";
import type { RulePolicyBinding } from "../domain/rulePolicy";
import {
    createRuleSummaryPresentation,
    type RuleSimplifiedLogicEdit,
    type RuleSimplifiedLogicEditorModel,
    type RuleSimplifiedLogicFieldOption,
    type RuleSummaryAst,
    type RuleSummaryPresentation,
    type RuleSummaryPresentationSource,
    type RuleSummaryPresentationStatus,
    type RuleSummaryValue,
} from "../domain/ruleSummary";
import {
    identityRuleRowMapper,
    type ExecutableStorageBackedConfig,
    type RuleConfigFactoryContext,
    type RuleConfigFactoryPort,
    type RuleDemoDataProjection,
    type RuleDemoProjectionPort,
    type RuleIntegrationEvaluationContext as BaseRuleIntegrationEvaluationContext,
    type RuleRowMapperPort,
    type RuleStorageCodecPort,
    type RuleSummaryEditPort,
} from "../application/ports/RuleIntegrationPorts";
import type { RuleSuggestionCommandPort } from "../application/ports/RuleSuggestionCommandPort";

export type {
    RuleEditorSurface,
    RuleIntegrationStatus,
} from "../domain/ruleCatalog";
export {
    describeRuleIntegrationStatus,
    isIntegratedStatus,
} from "../domain/ruleCatalog";
export type { RuleRuntimeEvidence } from "../domain/ruleEvaluation";
export type { RulePolicyBinding } from "../domain/rulePolicy";
export {
    createRuleSummaryPresentation,
} from "../domain/ruleSummary";
export type {
    RuleSimplifiedLogicEdit,
    RuleSimplifiedLogicEditorModel,
    RuleSimplifiedLogicFieldOption,
    RuleSummaryAst,
    RuleSummaryPresentation,
    RuleSummaryPresentationSource,
    RuleSummaryPresentationStatus,
    RuleSummaryValue,
} from "../domain/ruleSummary";
export {
    identityRuleRowMapper,
} from "../application/ports/RuleIntegrationPorts";
export type {
    RuleConfigFactoryContext,
    RuleConfigFactoryPort,
    RuleDemoDataProjection,
    RuleRowMapperPort,
    RuleStorageCodecPort,
} from "../application/ports/RuleIntegrationPorts";

export type AppGlueStorageBackedConfig = ExecutableStorageBackedConfig;
export type RuleIntegrationComparison = RuleEvaluationComparison;
export type RuleIntegrationEvaluation = RuleEvaluationResult;
export type RuleIntegrationEvaluationContext = BaseRuleIntegrationEvaluationContext<DemoRuleLogicDefinition>;
export type RuleEvaluatorPort<
    Config extends AppGlueStorageBackedConfig = AppGlueStorageBackedConfig,
    RuntimeRow = unknown,
> = {
    evaluate: (config: Config, row: RuntimeRow, context?: RuleIntegrationEvaluationContext) => RuleIntegrationEvaluation;
};
export type RuleSummaryPresenterPort<Config extends AppGlueStorageBackedConfig = AppGlueStorageBackedConfig> = {
    renderSummary: (config: Config, context?: RuleIntegrationEvaluationContext) => RuleSummaryPresentation;
};
export type RuleSimplifiedLogicEditorPort<Config extends AppGlueStorageBackedConfig = AppGlueStorageBackedConfig> =
    RuleSummaryEditPort<Config>;
export type RuleDemoDataProjectionPort<Config extends AppGlueStorageBackedConfig = AppGlueStorageBackedConfig> =
    RuleDemoProjectionPort<Config>;

export type RuleIntegrationPorts<
    Config extends AppGlueStorageBackedConfig = AppGlueStorageBackedConfig,
    DemoRow = unknown,
    RuntimeRow = DemoRow,
> = {
    configFactory: RuleConfigFactoryPort<Config>;
    storageCodec: RuleStorageCodecPort<Config>;
    rowMapper: RuleRowMapperPort<DemoRow, RuntimeRow>;
    evaluator: RuleEvaluatorPort<Config, RuntimeRow>;
    summaryPresenter?: RuleSummaryPresenterPort<Config>;
    simplifiedLogicEditor?: RuleSimplifiedLogicEditorPort<Config>;
    demoDataProjection?: RuleDemoDataProjectionPort<Config>;
    suggestionCommands?: RuleSuggestionCommandPort<Config>;
};

export type DemoRuleIntegrationAdapter<
    Config extends AppGlueStorageBackedConfig = AppGlueStorageBackedConfig,
    DemoRow = unknown,
    RuntimeRow = DemoRow,
> = {
    ruleId: string;
    status: Exclude<RuleIntegrationStatus, "demo_only">;
    appGlueExecutableType: string;
    editorSurface: RuleEditorSurface;
    policyBinding?: RulePolicyBinding;
    ports: RuleIntegrationPorts<Config, DemoRow, RuntimeRow>;
};

export type AnyDemoRuleIntegrationAdapter = DemoRuleIntegrationAdapter<any, any>;

export type RuleIntegrationValidationIssue = {
    ruleId: string;
    message: string;
};

export type RuleIntegrationRegistry = Record<string, AnyDemoRuleIntegrationAdapter>;

export type RuleIntegrationRuntimeSamples = Record<
    string,
    {
        passingRow: unknown;
        routedRow: unknown;
    }
>;

export const createRuleIntegrationConfig = <Config extends AppGlueStorageBackedConfig>(
    adapter: DemoRuleIntegrationAdapter<Config, any, any>,
    context: RuleConfigFactoryContext = {},
): Config => adapter.ports.configFactory.createConfig(context);

export const serializeRuleIntegrationConfig = <Config extends AppGlueStorageBackedConfig>(
    adapter: DemoRuleIntegrationAdapter<Config, any, any>,
    config: Config,
): object => adapter.ports.storageCodec.serialize(config);

export const hydrateRuleIntegrationConfig = <Config extends AppGlueStorageBackedConfig>(
    adapter: DemoRuleIntegrationAdapter<Config, any, any>,
    storageData: object,
): Config => adapter.ports.storageCodec.hydrate(storageData);

export const evaluateRuleIntegration = <
    Config extends AppGlueStorageBackedConfig,
    DemoRow,
    RuntimeRow = DemoRow,
>(
    adapter: DemoRuleIntegrationAdapter<Config, DemoRow, RuntimeRow>,
    config: Config,
    row: DemoRow,
    context?: RuleIntegrationEvaluationContext,
): RuleIntegrationEvaluation => adapter.ports.evaluator.evaluate(config, adapter.ports.rowMapper.toRuntimeRow(row), context);

export const getRuleIntegrationSummaryPresentation = <Config extends AppGlueStorageBackedConfig>(
    adapter: DemoRuleIntegrationAdapter<Config, any, any>,
    config: Config,
    context?: RuleIntegrationEvaluationContext,
): RuleSummaryPresentation | undefined => adapter.ports.summaryPresenter?.renderSummary(config, context);

export const renderRuleIntegrationSummary = <Config extends AppGlueStorageBackedConfig>(
    adapter: DemoRuleIntegrationAdapter<Config, any, any>,
    config: Config,
    context?: RuleIntegrationEvaluationContext,
): string[] | undefined => getRuleIntegrationSummaryPresentation(adapter, config, context)?.lines;

export const getRuleIntegrationSimplifiedLogicEditorModel = <Config extends AppGlueStorageBackedConfig>(
    adapter: DemoRuleIntegrationAdapter<Config, any, any>,
    config: Config,
    context?: RuleIntegrationEvaluationContext,
): RuleSimplifiedLogicEditorModel | null => adapter.ports.simplifiedLogicEditor?.getEditorModel(config, context) ?? null;

export const applyRuleIntegrationSimplifiedLogicEdit = <Config extends AppGlueStorageBackedConfig>(
    adapter: DemoRuleIntegrationAdapter<Config, any, any>,
    config: Config,
    edit: RuleSimplifiedLogicEdit,
): Config => adapter.ports.simplifiedLogicEditor?.applyEdit(config, edit) ?? config;

export const projectRuleIntegrationDemoData = <Config extends AppGlueStorageBackedConfig>(
    adapter: DemoRuleIntegrationAdapter<Config, any, any>,
    config: Config,
): RuleDemoDataProjection | undefined => adapter.ports.demoDataProjection?.projectDemoData(config);

export const validateRuleIntegrationRegistry = (
    definitions: DemoRuleLogicDefinition[],
    registry: RuleIntegrationRegistry,
): RuleIntegrationValidationIssue[] => {
    const issues: RuleIntegrationValidationIssue[] = [];

    definitions.forEach((definition) => {
        if (!isIntegratedStatus(definition.integrationStatus)) return;

        const adapter = registry[definition.ruleId];
        if (!adapter) {
            issues.push({
                ruleId: definition.ruleId,
                message: `Rule is marked ${definition.integrationStatus} but has no registered AppGlue adapter.`,
            });
            return;
        }

        if (adapter.status !== definition.integrationStatus) {
            issues.push({
                ruleId: definition.ruleId,
                message: `Adapter status ${adapter.status} does not match rule status ${definition.integrationStatus}.`,
            });
        }

        if (
            definition.integrationStatus === "native_appglue" &&
            adapter.editorSurface !== "native_appglue_editor" &&
            adapter.editorSurface !== "native_text_merge_builder"
        ) {
            issues.push({
                ruleId: definition.ruleId,
                message: "Native AppGlue rules must open a native AppGlue editor surface.",
            });
        }

        if (
            definition.integrationStatus === "adapter_backed" &&
            adapter.appGlueExecutableType === "RuleTableConfiguration" &&
            adapter.editorSurface !== "native_rule_table_builder"
        ) {
            issues.push({
                ruleId: definition.ruleId,
                message: "RuleTableConfiguration-backed rules must open the native AppGlue RuleTableBuilder.",
            });
        }

        if (
            definition.integrationStatus === "adapter_backed" &&
            adapter.appGlueExecutableType === "ValueTableConfiguration" &&
            adapter.editorSurface !== "native_value_table_builder"
        ) {
            issues.push({
                ruleId: definition.ruleId,
                message: "ValueTableConfiguration-backed rules must open the native AppGlue ValueTableRuleBuilder.",
            });
        }

        if (
            adapter.appGlueExecutableType === "TextMergeConfiguration" &&
            adapter.editorSurface !== "native_text_merge_builder"
        ) {
            issues.push({
                ruleId: definition.ruleId,
                message: "TextMergeConfiguration-backed rules must open the native AppGlue TextMergeBuilder.",
            });
        }

        if (typeof adapter.appGlueExecutableType !== "string" || !adapter.appGlueExecutableType.trim()) {
            issues.push({
                ruleId: definition.ruleId,
                message: "Integrated rules must declare the AppGlue executable/configuration type they use.",
            });
        }

        if (!adapter.ports?.configFactory) {
            issues.push({
                ruleId: definition.ruleId,
                message: "Integrated rules must expose a config factory port.",
            });
        }

        if (!adapter.ports?.storageCodec) {
            issues.push({
                ruleId: definition.ruleId,
                message: "Integrated rules must expose a storage codec port.",
            });
        }

        if (!adapter.ports?.rowMapper) {
            issues.push({
                ruleId: definition.ruleId,
                message: "Integrated rules must expose a row mapper port.",
            });
        }

        if (!adapter.ports?.evaluator) {
            issues.push({
                ruleId: definition.ruleId,
                message: "Integrated rules must expose an evaluator port.",
            });
        }
    });

    Object.values(registry).forEach((adapter) => {
        const definition = definitions.find((candidate) => candidate.ruleId === adapter.ruleId);
        if (!definition) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter is registered for a rule that is not in the demo rule catalog.",
            });
            return;
        }

        if (!isIntegratedStatus(definition.integrationStatus)) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Demo-only rules must not register AppGlue adapters.",
            });
        }
    });

    return issues;
};

const hasAppGlueStorageApi = (config: unknown): config is AppGlueStorageBackedConfig =>
    !!config &&
    typeof config === "object" &&
    typeof (config as AppGlueStorageBackedConfig).getStorageData === "function" &&
    typeof (config as AppGlueStorageBackedConfig).setStorageData === "function";

type AppGlueDesignValidatableConfig = AppGlueStorageBackedConfig & {
    getDesignValidationIssues: () => Array<{ issue?: string }>;
};

const hasAppGlueDesignValidationApi = (config: unknown): config is AppGlueDesignValidatableConfig =>
    hasAppGlueStorageApi(config) &&
    typeof (config as AppGlueDesignValidatableConfig).getDesignValidationIssues === "function";

const formatDesignValidationIssues = (config: AppGlueDesignValidatableConfig): string[] =>
    config
        .getDesignValidationIssues()
        .map((issue) => issue.issue ?? "Unknown AppGlue design validation issue");

export const validateRuleIntegrationAdapterContract = (
    adapter: DemoRuleIntegrationAdapter,
    sampleRow: unknown,
): RuleIntegrationValidationIssue[] => {
    const issues: RuleIntegrationValidationIssue[] = [];

    try {
        if (!adapter.ports?.configFactory) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter must expose a config factory port.",
            });
            return issues;
        }
        if (!adapter.ports?.storageCodec) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter must expose a storage codec port.",
            });
            return issues;
        }
        if (!adapter.ports?.rowMapper) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter must expose a row mapper port.",
            });
            return issues;
        }
        if (!adapter.ports?.evaluator) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter must expose an evaluator port.",
            });
            return issues;
        }

        const config = createRuleIntegrationConfig(adapter);
        if (!hasAppGlueStorageApi(config)) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter config must expose AppGlue getStorageData() and setStorageData().",
            });
            return issues;
        }
        if (hasAppGlueDesignValidationApi(config)) {
            const designIssues = formatDesignValidationIssues(config);
            if (designIssues.length > 0) {
                issues.push({
                    ruleId: adapter.ruleId,
                    message: `Adapter config has AppGlue design validation issues: ${designIssues.join("; ")}`,
                });
            }
        }

        const directStorageData = config.getStorageData();
        if (!directStorageData || typeof directStorageData !== "object") {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter config getStorageData() must return AppGlue storage data.",
            });
        }

        const storageData = serializeRuleIntegrationConfig(adapter, config);
        if (!storageData || typeof storageData !== "object") {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter serialize must return AppGlue storage data.",
            });
            return issues;
        }

        const hydrated = hydrateRuleIntegrationConfig(adapter, storageData);
        if (!hasAppGlueStorageApi(hydrated)) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter hydrate must return a config with AppGlue storage APIs.",
            });
            return issues;
        }
        hydrated.setStorageData(storageData);
        if (hasAppGlueDesignValidationApi(hydrated)) {
            const designIssues = formatDesignValidationIssues(hydrated);
            if (designIssues.length > 0) {
                issues.push({
                    ruleId: adapter.ruleId,
                    message: `Adapter hydrated config has AppGlue design validation issues: ${designIssues.join("; ")}`,
                });
            }
        }
        const hydratedStorageData = hydrated.getStorageData();
        if (!hydratedStorageData || typeof hydratedStorageData !== "object") {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter hydrate must preserve AppGlue storage data through getStorageData().",
            });
            return issues;
        }

        const runtimeRow = adapter.ports.rowMapper.toRuntimeRow(sampleRow);
        if (runtimeRow === undefined || runtimeRow === null) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter row mapper must return runtime row data.",
            });
            return issues;
        }

        const evaluation = adapter.ports.evaluator.evaluate(hydrated, runtimeRow);
        if (!evaluation || typeof evaluation.reasonCode !== "string" || typeof evaluation.explanation !== "string") {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter evaluate must return a reason code and explanation.",
            });
        }

        if (
            !evaluation ||
            !["pass", "flagged", "rejected", "skipped"].includes(evaluation.status) ||
            !Array.isArray(evaluation.comparisons)
        ) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter evaluate must return a demo status and display comparisons.",
            });
        }

        if (
            evaluation?.runtimeEvidence?.runtime !== "appglue_runtime" ||
            evaluation.runtimeEvidence.executableType !== adapter.appGlueExecutableType
        ) {
            issues.push({
                ruleId: adapter.ruleId,
                message: "Adapter evaluate must return AppGlue runtime evidence matching its executable type.",
            });
        }
    } catch (error) {
        issues.push({
            ruleId: adapter.ruleId,
            message: error instanceof Error ? error.message : "Adapter contract threw an unknown error.",
        });
    }

    return issues;
};

export const validateNativeRuleRuntimeRouting = (
    definitions: DemoRuleLogicDefinition[],
    sourceText: string,
): RuleIntegrationValidationIssue[] =>
    definitions
        .filter((definition) => isIntegratedStatus(definition.integrationStatus))
        .flatMap((definition) => {
            const ruleIdPattern = definition.ruleId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const hardcodedRulePattern = new RegExp(`RULES_BY_ID\\[["']${ruleIdPattern}["']\\]`);

            if (!hardcodedRulePattern.test(sourceText)) return [];

            return [
                {
                    ruleId: definition.ruleId,
                    message: "Integrated AppGlue rules must not remain in demo-only event branching.",
                },
            ];
        });

export const validateRuleIntegrationRuntimeSamples = (
    definitions: DemoRuleLogicDefinition[],
    registry: RuleIntegrationRegistry,
    samples: RuleIntegrationRuntimeSamples,
): RuleIntegrationValidationIssue[] =>
    definitions
        .filter((definition) => isIntegratedStatus(definition.integrationStatus))
        .flatMap((definition) => {
            const adapter = registry[definition.ruleId];
            if (!adapter) return [];

            const ruleSamples = samples[definition.ruleId];
            if (!ruleSamples) {
                return [
                    {
                        ruleId: definition.ruleId,
                        message: "Integrated rules must provide passing and routed sample rows for runtime guardrails.",
                    },
                ];
            }

            const config = createRuleIntegrationConfig(adapter);
            const passingEvaluation = evaluateRuleIntegration(adapter, config, ruleSamples.passingRow);
            const routedEvaluation = evaluateRuleIntegration(adapter, config, ruleSamples.routedRow);
            const issues: RuleIntegrationValidationIssue[] = [];

            if (!passingEvaluation.passed) {
                issues.push({
                    ruleId: definition.ruleId,
                    message: "Integrated rule passing sample must pass through AppGlue runtime.",
                });
            }

            if (routedEvaluation.passed) {
                issues.push({
                    ruleId: definition.ruleId,
                    message: "Integrated rule routed sample must fail through AppGlue runtime.",
                });
            }

            [passingEvaluation, routedEvaluation].forEach((evaluation) => {
                if (
                    evaluation.runtimeEvidence?.runtime !== "appglue_runtime" ||
                    evaluation.runtimeEvidence.executableType !== adapter.appGlueExecutableType
                ) {
                    issues.push({
                        ruleId: definition.ruleId,
                        message: "Integrated rule samples must return AppGlue runtime evidence.",
                    });
                }
            });

            return issues;
        });
